import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from '../Button';
import { IconChevronDown, IconChevronUp } from '../icons/Icons';
import { useId, useResizeDetection } from '../../../hooks';
import { PlaceholderText } from '../Placeholder';

const ACCORDION_ANIMATION_TIME = 350;

export default function ReadMoreBlock({
    isLoading,
    id,
    children,
    className,
    linesToShow = 3,
    lineHeight = 23.8,
    buttonAriaLabel = 'Read more',
    onChange = undefined
}) {
    const outerDivRef = useRef();
    const innerDivRef = useRef();
    const isInitialLoad = useRef(true);
    const [isExpanded, setExpanded] = useState(false);
    const [showButton, setShowButton] = useState(true);
    const computedId = useId(id);
    const isResizing = useResizeDetection();

    // This is the height of the "preview" when the block is collapsed
    // Subtracting a couple of pixels to completely hide all character ascenders
    const collapsedMaxHeight = `${linesToShow * lineHeight - 2}px`;

    /**
     * When isExpanded changes, set the heights of the content block so we can animate it
     */
    useEffect(() => {
        let timeout;

        if (!isLoading) {
            const contentHeight = innerDivRef.current.clientHeight;
            const expandedMaxHeight = contentHeight ? `${contentHeight}px` : 'none';

            if (showButton && isExpanded) {
                clearTimeout(timeout);

                // Set the container height to it's full height. CSS handles the animation
                outerDivRef.current.style.maxHeight = expandedMaxHeight;

                // After the animation, remove the maxHeight so the content can
                // respond to window size changes
                timeout = setTimeout(() => {
                    outerDivRef.current.style.maxHeight = 'none';
                }, ACCORDION_ANIMATION_TIME + 100);
            }

            if (showButton && !isExpanded) {
                clearTimeout(timeout);

                // Apply the expanded height so we can transition maxHeight
                outerDivRef.current.style.maxHeight = expandedMaxHeight;

                if (isInitialLoad) {
                    outerDivRef.current.style.maxHeight = collapsedMaxHeight;
                    isInitialLoad.current = false;
                } else {
                    // Slight arbitrary delay to wait for the DOM to complete the previous action
                    // Then set the height to the collapsed height. CSS handles the animation
                    timeout = setTimeout(() => {
                        outerDivRef.current.style.maxHeight = collapsedMaxHeight;
                    }, 10);
                }
            }
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [isLoading, isExpanded, collapsedMaxHeight, showButton]);

    /**
     * Determine if content is short so we don't need to show the 'read more' button
     */
    useEffect(() => {
        if (!isLoading) {
            const contentHeight = innerDivRef.current.clientHeight;
            const minifiedHeight = linesToShow * lineHeight;

            // If the content is the same or shorter than the collapsed height,
            // then we don't need the accordion at all
            if (contentHeight < minifiedHeight) {
                setShowButton(false);
                setExpanded(false);
                outerDivRef.current.style.maxHeight = 'none';
            } else {
                setShowButton(true);
            }
        }
    }, [lineHeight, linesToShow, isResizing, isLoading]);

    const handleButtonClick = () => {
        // If defined run the callback
        if (onChange) {
            onChange(!isExpanded);
        }

        setExpanded(!isExpanded);
    };

    if (isLoading) {
        return (
            <div>
                <PlaceholderText lines={linesToShow} />
                <PlaceholderText fontSize={12} lineHeight={24} lines={1} width="80px" />
            </div>
        );
    }

    return (
        <div
            data-component="ReadMoreBlock"
            className={cx('sarsa-read-more-block-wrapper', className, {
                collapsed: !isExpanded,
            })}
        >
            <div
                id={computedId}
                ref={outerDivRef}
                className="sarsa-read-more-block"
                style={{ maxHeight: collapsedMaxHeight }}
                onFocus={() => {
                    setTimeout(() => {
                        setExpanded(true);
                    }, 0);
                }}
            >
                <div className="sarsa-read-more-block-inner" ref={innerDivRef}>
                    {children}
                </div>
            </div>

            {showButton && (
                <div className="button-wrap">
                    <Button
                        size="xs"
                        appearance="link"
                        aria-controls={computedId}
                        aria-label={buttonAriaLabel}
                        aria-expanded={isExpanded}
                        onClick={handleButtonClick}
                    >
                        {isExpanded && (
                            <span className="read-more-with-icon">
                                <IconChevronUp size="sm" /> <span>Read Less</span>
                            </span>
                        )}
                        {!isExpanded && (
                            <span className="read-more-with-icon">
                                <IconChevronDown size="sm" /> <span>Read More</span>
                            </span>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}

ReadMoreBlock.propTypes = {
    isLoading: PropTypes.bool,

    /** Id assigned to the collapse/expand container. Used for 508 compliance */
    id: PropTypes.string,

    /** Contents that are collapsed/expanded */
    children: PropTypes.node,

    /** Number of lines of text to show in the collapsed state */
    linesToShow: PropTypes.number,

    /** Line height of the children text in pixels. Used to calculate the
     * height of the container when in its collapsed state */
    lineHeight: PropTypes.number,

    /** The accessibility label of the "Read More" button. If more than one
     * ReadMoreBlock is used on a page, then this prop must be used to make
     * all the "Read More" buttons have unique accessibility labels */
    buttonAriaLabel: PropTypes.string,

    /** Additional class added to the outer-most container */
    className: PropTypes.string,

    /** Method executed when the contents are collapsed/expanded. It's given
     * a boolean to indicate if the content is being expanded or collapsed */
    onChange: PropTypes.func
};
